import {
  type NotificationItem,
  type NotificationKind,
  addDays,
  timeOf,
  todayIso,
  useAuth,
  useMarkNotificationsRead,
  useNotifications,
  useViewContext,
} from '@shkola/api';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import {
  CalendarDotsIcon,
  CaretRightIcon,
  ChartBarIcon,
  ChatCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  ConfettiIcon,
  type FilledIcon,
  HeartIcon,
  ListChecksIcon,
  MegaphoneIcon,
  NotePencilIcon,
  PuzzlePieceIcon,
  SirenIcon,
  StarIcon,
  TrophyIcon,
  UsersThreeIcon,
  VideoCameraIcon,
} from '../design/icons';
import { capitalize, dayLong } from '../student/home/model';

/*
 * «Сповіщення» in «Форми 2» (28.10): the title on the paper with how many are new and the one button to
 * mark them all; one white block — on top the kinds as chips to narrow the list (as the subjects in
 * «Домашні»), then the feed by day, each with its small icon in its kind's gradient; the new ones in the
 * full ink with an orange dot. A tap opens the right screen for the right child.
 */

const SCREENS: Record<string, string> = {
  grades: '/grades',
  homework: '/homework',
  announcements: '/announcements',
  messages: '/messages',
  alarm: '/alarm',
  requests: '/requests',
  absent: '/requests',
  meetings: '/meetings',
  lesson: '/schedule',
  events: '/events',
  schedule: '/schedule',
  polls: '/polls',
  achievements: '/achievements',
  quizzes: '/quizzes',
  care: '/care',
  clubs: '/clubs',
  forms: '/forms',
  season: '/season',
};

/** A kind: its icon, its colour, and the section it belongs to for the chips. */
const KINDS: Record<NotificationKind, { icon: FilledIcon; tone: Grad; group: Group }> = {
  grade: { icon: StarIcon, tone: 'butter', group: 'grades' },
  homework: { icon: NotePencilIcon, tone: 'sky', group: 'homework' },
  homework_review: { icon: CheckCircleIcon, tone: 'mint', group: 'homework' },
  announcement: { icon: MegaphoneIcon, tone: 'pink', group: 'announcements' },
  absence: { icon: ClockIcon, tone: 'apricot', group: 'other' },
  schedule: { icon: CalendarDotsIcon, tone: 'cobalt', group: 'schedule' },
  alarm: { icon: SirenIcon, tone: 'coral', group: 'other' },
  message: { icon: ChatCircleIcon, tone: 'lavender', group: 'messages' },
  meeting: { icon: UsersThreeIcon, tone: 'apricot', group: 'other' },
  lesson: { icon: VideoCameraIcon, tone: 'cobalt', group: 'schedule' },
  event: { icon: ConfettiIcon, tone: 'pink', group: 'events' },
  poll: { icon: ChartBarIcon, tone: 'aqua', group: 'other' },
  achievement: { icon: TrophyIcon, tone: 'butter', group: 'other' },
  quiz: { icon: PuzzlePieceIcon, tone: 'lavender', group: 'other' },
  digest: { icon: CalendarDotsIcon, tone: 'sand', group: 'other' },
  care: { icon: HeartIcon, tone: 'pink', group: 'other' },
  club: { icon: UsersThreeIcon, tone: 'mint', group: 'other' },
  form: { icon: ListChecksIcon, tone: 'sky', group: 'other' },
};

type Group = 'grades' | 'homework' | 'announcements' | 'messages' | 'schedule' | 'events' | 'other';
const GROUPS: ReadonlyArray<Group> = [
  'grades',
  'homework',
  'announcements',
  'messages',
  'schedule',
  'events',
  'other',
];

const lookOf = (kind: string) =>
  KINDS[kind as NotificationKind] ?? {
    icon: CalendarDotsIcon,
    tone: 'stone' as Grad,
    group: 'other' as Group,
  };

/** All my member ids, across schools: the feed is per person. */
export function useAllMemberIds(): string[] {
  const { me } = useAuth();
  return (me?.memberships ?? []).map((m) => m.member_id);
}

export function useUnreadCount(): number {
  const notifications = useNotifications(useAllMemberIds());
  return (notifications.data ?? []).filter((n) => !n.readAt).length;
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const memberIds = useAllMemberIds();
  const notifications = useNotifications(memberIds);
  const markRead = useMarkNotificationsRead();
  const { contexts, select } = useViewContext();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group>();
  const all = notifications.data ?? [];
  const unread = all.filter((n) => !n.readAt);
  const today = todayIso();
  const yesterday = addDays(today, -1);

  // a tap opens the right screen for the right child
  const open = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate([n.id]);
    const student = n.link.student;
    const target = contexts.find(
      (c) => (c.kind === 'guardian' || c.kind === 'student') && c.child.memberId === student,
    );
    if (target) select(target.key);
    if (n.link.screen === 'lesson' && n.link.lesson) {
      void navigate({ to: '/lesson/$lessonId', params: { lessonId: n.link.lesson } });
      return;
    }
    if (n.link.screen === 'care') {
      void navigate({ to: '/care', search: n.link.thread ? { thread: n.link.thread } : {} });
      return;
    }
    const to = SCREENS[n.link.screen ?? ''];
    if (to) void navigate({ to });
  };

  // the chips: only the kinds that are there, with how many are new
  const groups = GROUPS.map((g) => {
    const items = all.filter((n) => lookOf(n.kind).group === g);
    return { g, count: items.length, fresh: items.filter((n) => !n.readAt).length };
  }).filter((x) => x.count > 0);
  const shown = group ? all.filter((n) => lookOf(n.kind).group === group) : all;
  const days = new Map<string, NotificationItem[]>();
  for (const n of shown) {
    const day = n.createdAt.slice(0, 10);
    days.set(day, [...(days.get(day) ?? []), n]);
  }
  const dayTitle = (day: string) =>
    day === today
      ? t('events.today')
      : day === yesterday
        ? capitalize(t('studentHome.glass.school.yesterday'))
        : dayLong(day);
  const label = (g: Group) => (g === 'other' ? t('events.kinds.other') : t(`nav.${g}`));
  const chip = (on: boolean) =>
    `inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('notifications.title')}
        note={unread.length > 0 ? t('notifications.unread', { count: unread.length }) : undefined}
      >
        {unread.length > 0 && (
          <button
            type="button"
            className={`${pillClass('light')} max-lg:w-11 max-lg:justify-center max-lg:px-0 disabled:opacity-50`}
            disabled={markRead.isPending}
            onClick={() => markRead.mutate(unread.map((n) => n.id))}
          >
            <span className="max-lg:sr-only">{t('notifications.markAll')}</span>
            <PillIcon icon={CheckIcon} />
          </button>
        )}
      </PageHead>
      <Block>
        {notifications.isPending ? (
          <Spinner />
        ) : all.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center">
            <TaskArt kind="done" tone="lime" size={96} />
            <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
              {t('notifications.empty')}
            </p>
          </div>
        ) : (
          <>
            {groups.length > 1 && (
              <div className="no-scrollbar -mx-5 mb-2 flex gap-1.5 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
                <button
                  type="button"
                  aria-pressed={!group}
                  onClick={() => setGroup(undefined)}
                  className={chip(!group)}
                  style={{ backgroundColor: !group ? '#E9E5DC' : PAPER, color: INK }}
                >
                  {t('clubs.all')}
                  <span style={{ color: INK_FAINT }}>{all.length}</span>
                </button>
                {groups.map(({ g, count, fresh }) => {
                  const on = g === group;
                  return (
                    <button
                      key={g}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setGroup(on ? undefined : g)}
                      className={chip(on)}
                      style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                    >
                      {label(g)}
                      <span style={{ color: fresh > 0 ? '#D9471A' : INK_FAINT }}>
                        {fresh > 0 ? fresh : count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {[...days].map(([day, items]) => (
              <section key={day} className="mt-5">
                <h2
                  className="mb-1 flex items-baseline gap-2 text-[15px] font-medium"
                  style={{ color: INK }}
                >
                  {dayTitle(day)}
                  <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
                    {items.length}
                  </span>
                </h2>
                <ul className="flex flex-col">
                  {items.map((n) => {
                    const look = lookOf(n.kind);
                    const Icon = look.icon;
                    return (
                      <li key={n.id} className="border-t border-[#E4E0D7] first:border-t-0">
                        <button
                          type="button"
                          onClick={() => open(n)}
                          className="group -mx-2 flex w-[calc(100%+16px)] items-center gap-3.5 rounded-[18px] px-2 py-3 text-left transition-colors hover:bg-[#F5F3EF]"
                        >
                          <span
                            className="flex size-10 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundImage: grad(look.tone), color: GRAD[look.tone].ink }}
                          >
                            <Icon size={18} weight="fill" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block text-[15px] leading-[21px] ${n.readAt ? '' : 'font-medium'}`}
                              style={{ color: n.readAt ? INK_SOFT : INK }}
                            >
                              {n.title}
                            </span>
                            {n.body && (
                              <span
                                className="mt-0.5 line-clamp-2 block max-w-[760px] text-[13.5px] leading-5"
                                style={{ color: INK_SOFT }}
                              >
                                {n.body}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            {!n.readAt && (
                              <span
                                className="size-2 rounded-full"
                                style={{ backgroundColor: '#FF6A3D' }}
                                aria-hidden
                              />
                            )}
                            <span className="text-[12.5px]" style={{ color: INK_FAINT }}>
                              {timeOf(n.createdAt)}
                            </span>
                            <CaretRightIcon
                              size={12}
                              weight="bold"
                              aria-hidden
                              color={INK}
                              className="hidden opacity-0 transition-opacity group-hover:opacity-100 sm:block"
                            />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </>
        )}
      </Block>
    </div>
  );
}
